import { Button, ChevronRight, Plus, RotateCcw, GripVertical, Input, cn } from "@app/ui";
import { useRef, useState, type ReactNode } from "react";

import { useI18n } from "../../lib/useI18n.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { animate, motion, Reorder, springs, useDragControls, useMotionValue, useReducedMotion } from "../../lib/motion/index.js";
import type { useDragClickGuard } from "../../lib/useDragClickGuard.js";
import type { BoardTargetRef } from "../../stores/index.js";
import { REPARENT_DRAG_THRESHOLD } from "../topics/tree.js";
import { useArmColorTarget } from "./EntityColorControl.js";
import { EmojiPicker } from "./emoji/EmojiPicker.js";

/** How far (px) the row visually follows a horizontal drag before resistance
 *  caps it — comfortably past `REPARENT_DRAG_THRESHOLD` so the row is still
 *  visibly "committing" right as the threshold triggers. */
const DRAG_X_VISUAL_CAP = 56;

/** One draggable row in an Organize panel: grip · big color swatch (click
 *  arms the palette board) · optional revert · optional clickable emoji ·
 *  inline-editable name · per-entity actions. Nothing needs to expand on
 *  desktop anymore — `midActions` (e.g. calendar default/hidden) and
 *  `deleteAction` are always inline, and `expandableActions` (per-view Show in
 *  controls) join them there too. On mobile, `expandableActions` collapses
 *  behind a chevron instead so the row still fits a phone width; `midActions`
 *  and `deleteAction` stay inline on mobile as well. The expanded area and any
 *  nested child levels render inside the Reorder.Item so they travel with the
 *  row while dragging. `onHorizontalDragEnd` (topics only) reports the
 *  released drag's total x offset so the caller can indent/outdent into a
 *  parent/sub-topic. */
export function EntityRow({
  id,
  depth = 0,
  guard,
  indexLabel,
  swatchHex,
  colorTarget,
  boardAnchorId,
  colorOverridden,
  onRevertColor,
  emoji,
  onEmojiChange,
  name,
  onRename,
  midActions,
  expandableActions,
  deleteAction,
  expanded,
  onToggleExpand,
  onHorizontalDragEnd,
  children,
}: {
  id: string;
  depth?: number;
  guard: ReturnType<typeof useDragClickGuard>;
  /** 1-based list position shown as a badge; also labels this entity's color
   *  marker on the palette board (calendars only). Omit to render no badge. */
  indexLabel?: number;
  swatchHex: string;
  colorTarget: BoardTargetRef;
  /** SettingsAnchor id of the board card to reveal on mobile after arming. */
  boardAnchorId?: string;
  colorOverridden?: boolean;
  onRevertColor?: () => void;
  /** Topics only — omit to render no emoji slot at all. */
  emoji?: string | null;
  onEmojiChange?: (value: string) => void;
  name: string;
  onRename: (name: string) => void;
  /** Always inline on both desktop and mobile (e.g. calendar default/hidden). */
  midActions?: ReactNode;
  /** Inline on desktop; collapses behind the mobile chevron (per-view Show in controls). */
  expandableActions?: ReactNode;
  deleteAction: ReactNode;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onHorizontalDragEnd?: (offsetX: number) => void;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  const mobile = useMobileUx();
  const controls = useDragControls();
  const { armed: colorArmed, handleArm: onColorClick } = useArmColorTarget(colorTarget, boardAnchorId);
  const [pickerAnchor, setPickerAnchor] = useState<DOMRect | null>(null);
  const showChevron = mobile && expandableActions != null;
  // Reorder.Item locks visual movement to the y axis, so without this the row
  // only "teleports" into its new spot on release — this mirrors the drag's
  // real x offset (clamped) so indent/outdent reads as a drag, not a snap.
  const dragX = useMotionValue(0);
  const reducedMotion = useReducedMotion();
  // Real drags often settle back a few px right before release — deciding off
  // the FINAL offset made that read as "it needs way more drag than it does."
  // Track the furthest point reached instead, so once you've dragged past the
  // threshold it stays committed even if the pointer eases back before letting go.
  const peakOffsetX = useRef(0);
  const [pastThreshold, setPastThreshold] = useState(false);

  return (
    <Reorder.Item
      as="div"
      value={id}
      dragListener={false}
      dragControls={controls}
      // Reorder.Item defaults to `layout: true` (full FLIP incl. size), which
      // is right for the sibling-slide during a plain vertical drag — but
      // re-parenting a row changes a PARENT's own height (it now contains an
      // extra nested Reorder.Group), and FLIP-animating THAT size delta on a
      // flex column with text content is what read as the parent's row
      // flickering/squishing for a split second. Position-only layout still
      // slides siblings into their new slot but leaves each row's own size to
      // resolve instantly.
      layout="position"
      onDragStart={() => { guard.onDragStart(); peakOffsetX.current = 0; setPastThreshold(false); }}
      onDrag={(_event, info) => {
        if (!onHorizontalDragEnd) return;
        dragX.set(Math.max(-DRAG_X_VISUAL_CAP, Math.min(DRAG_X_VISUAL_CAP, info.offset.x)));
        if (Math.abs(info.offset.x) > Math.abs(peakOffsetX.current)) peakOffsetX.current = info.offset.x;
        const crossed = Math.abs(peakOffsetX.current) > REPARENT_DRAG_THRESHOLD;
        setPastThreshold((prev) => (prev === crossed ? prev : crossed));
      }}
      onDragEnd={(_event, info) => {
        guard.onDragEnd();
        onHorizontalDragEnd?.(peakOffsetX.current);
        setPastThreshold(false);
        if (reducedMotion) dragX.set(0);
        else animate(dragX, 0, springs.snappy);
      }}
      // position:relative + base zIndex:0 ensure whileDrag elevation works
      // correctly when dragging upward past items above.
      style={{ position: "relative", zIndex: 0 }}
      whileDrag={{ zIndex: 10 }}
    >
      <motion.div
        className={cn("flex items-center gap-2 rounded-md py-0.5", pastThreshold && "bg-primary/10 ring-2 ring-primary/40")}
        style={{ paddingLeft: depth * 20, x: dragX }}
        // Framer's Reorder fires a synthetic click on release, which can land on
        // whichever row element now sits under the cursor (the whole row travels
        // with the drag) — swallow it here instead of guarding every button.
        onClickCapture={(event) => { if (guard.suppressClick()) event.stopPropagation(); }}
      >
        <span
          title={t("organize.dragRow")}
          onPointerDown={(e) => controls.start(e)}
          className="shrink-0 cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </span>
        {indexLabel != null && (
          <span
            className="w-5 shrink-0 text-center text-xs font-semibold tabular-nums text-muted-foreground"
            title={t("organize.paletteNumber", { n: String(indexLabel) })}
          >
            {indexLabel}
          </span>
        )}
        <button
          type="button"
          onClick={onColorClick}
          aria-pressed={colorArmed}
          title={t("organize.colorTapNamed", { name })}
          aria-label={t("organize.colorTapNamed", { name })}
          className={cn(
            "h-7 w-7 shrink-0 rounded-full border border-border hover:opacity-80",
            colorArmed && "ring-2 ring-primary ring-offset-1 ring-offset-background",
          )}
          style={{ background: swatchHex }}
        />
        {colorOverridden && onRevertColor && (
          <Button type="button" size="sm" variant="ghost" onClick={onRevertColor} title={t("organize.colorRevert")} aria-label={t("organize.colorRevert")}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
        {onEmojiChange && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-haspopup="dialog"
            aria-expanded={pickerAnchor != null}
            onClick={(event) => setPickerAnchor(event.currentTarget.getBoundingClientRect())}
            title={t("lists.topicIconTitle")}
            aria-label={t("lists.topicIcon", { name })}
            className="h-7 w-9 shrink-0 px-0 text-base"
          >
            {emoji ? emoji : <Plus className="h-3.5 w-3.5 text-muted-foreground" />}
          </Button>
        )}
        <Input value={name} onChange={(event) => onRename(event.target.value)} className="min-w-0 flex-1" />
        {midActions}
        {!mobile && expandableActions}
        {deleteAction}
        {showChevron && (
          <button
            type="button"
            onClick={onToggleExpand}
            aria-expanded={expanded}
            aria-label={t(expanded ? "organize.collapseRow" : "organize.expandRow", { name })}
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronRight className={cn("h-4 w-4 transition-transform", expanded && "rotate-90")} />
          </button>
        )}
      </motion.div>
      {showChevron && expanded && (
        <div
          className="mb-1 mt-0.5 flex flex-wrap items-center gap-2 rounded-md border border-border bg-accent/30 p-3"
          style={{ marginLeft: depth * 20 + 24 }}
        >
          {expandableActions}
        </div>
      )}
      {children}
      {onEmojiChange && pickerAnchor && (
        <EmojiPicker
          anchor={pickerAnchor}
          current={emoji}
          name={name}
          onPick={(value) => onEmojiChange(value)}
          onRemove={() => onEmojiChange("")}
          onClose={() => setPickerAnchor(null)}
        />
      )}
    </Reorder.Item>
  );
}
